import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';

const IMG = `${process.env.PUBLIC_URL}/images`;

const heroNodes = [
  { key: 'emotion', label: 'Emotion', icon: 'heart', pos: 't' },
  { key: 'intelligence', label: 'Intelligence', icon: 'cpu', pos: 'tl' },
  { key: 'technology', label: 'Technology', icon: 'atom', pos: 'tr' },
  { key: 'commerce', label: 'Commerce', icon: 'shopping-bag', pos: 'bl' },
  { key: 'experiences', label: 'Experiences', icon: 'sparkles', pos: 'br' },
];

const pillars = [
  {
    icon: 'eye',
    title: 'Our Vision',
    desc: 'To be the global leader in intelligent experience ecosystems, empowering every celebration and enriching every life.',
  },
  {
    icon: 'target',
    title: 'Our Mission',
    desc: 'To transform the experience economy through technology, innovation and empathy — creating value for customers, vendors and communities.',
  },
  {
    icon: 'heart',
    title: 'Our Purpose',
    desc: 'To build meaningful connections and memorable experiences that bring joy, purpose and positive impact to the world.',
  },
  {
    icon: 'shield-check',
    title: 'Our Promise',
    desc: 'We are committed to trust, transparency, innovation and excellence in everything we build and every experience we touch.',
  },
];

const traits = [
  { icon: 'lightbulb', title: 'Vibrant', sub: 'Like Emotions' },
  { icon: 'shield-check', title: 'Resilient', sub: 'Like Our Purpose' },
  { icon: 'sparkles', title: 'Unique', sub: 'Like Every Experience' },
  { icon: 'trending-up', title: 'Ever-Growing', sub: 'Like Our Vision' },
];

const values = [
  {
    icon: 'lightbulb',
    title: 'Innovation',
    desc: 'We constantly explore, experiment and create what’s next.',
  },
  {
    icon: 'hand-heart',
    title: 'Empathy',
    desc: 'We put people first, understanding their needs and emotions.',
  },
  {
    icon: 'shield-check',
    title: 'Integrity',
    desc: 'We build with honesty, transparency and accountability.',
  },
  {
    icon: 'star',
    title: 'Excellence',
    desc: 'We pursue excellence in every detail and every interaction.',
  },
  {
    icon: 'users',
    title: 'Collaboration',
    desc: 'We believe that great things are built together.',
  },
  {
    icon: 'globe',
    title: 'Impact',
    desc: 'We are committed to creating positive impact at scale.',
  },
];

const bullets = [
  'Technology-First Platforms',
  'Marketplace & Network Effects',
  'AI & Data Intelligence',
  'Experience Commerce',
  'Scalable & Sustainable Growth',
];

const ecoNodes = [
  {
    key: 'platforms',
    label: 'Platforms',
    sub: 'Powerful digital platforms for every celebration',
    icon: 'layers',
    pos: 't',
  },
  {
    key: 'community',
    label: 'Community',
    sub: 'Empowering vendors and communities to grow together',
    icon: 'users',
    pos: 'tl',
  },
  {
    key: 'marketplace',
    label: 'Marketplace',
    sub: 'Connecting customers with trusted partners',
    icon: 'store',
    pos: 'tr',
  },
  {
    key: 'commerce',
    label: 'Commerce',
    sub: 'Seamless transactions and experience commerce',
    icon: 'shopping-bag',
    pos: 'bl',
  },
  {
    key: 'intelligence',
    label: 'Intelligence',
    sub: 'AI-driven insights for smarter decisions',
    icon: 'cpu',
    pos: 'br',
  },
];

const globalStats = [
  { icon: 'map-pin', value: '500+', label: 'Cities Reached' },
  { icon: 'globe', value: '10+', label: 'Countries (Vision)' },
  { icon: 'globe', value: 'Global', label: 'Mindset' },
  { icon: 'heart', value: 'Local', label: 'Heart' },
];

export default function About() {
  return (
    <div className="about">
      {/* =================== HERO =================== */}
      <section className="about-hero">
        <div
          className="about-hero__bg"
          style={{ backgroundImage: `url(${IMG}/about/about-1.jpeg)` }}
        />
        <div className="container container--wide about-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>About Caladium Systems</span>
          </div>

          <div className="about-hero__row">
            <div className="about-hero__copy">
              <span className="eyebrow">ABOUT CALADIUM SYSTEMS</span>
              <h1 className="about-hero__title">
                Built on Emotion.
                <br />
                Powered by Technology.
                <br />
                <span className="grad-pink">Designed for Impact.</span>
              </h1>
              <p className="about-hero__lead">
                Caladium Systems is an innovation company building intelligent
                ecosystems that transform the way the world celebrates, connects
                and creates experiences.
              </p>
              <button className="play-cta" type="button">
                <span className="play-cta__btn">
                  <Icon name="play" size={20} />
                </span>
                <span className="play-cta__text">
                  <strong>Watch Our Story</strong>
                  <span>2:45 min</span>
                </span>
              </button>
            </div>

            <div className="about-hero__visual">
              <div className="orbit orbit--big">
                <span className="orbit__ring orbit__ring--1" />
                <span className="orbit__ring orbit__ring--2" />
                <span className="orbit__ring orbit__ring--3" />
                <div className="orbit__core orbit__core--lotus">
                  <img src={`${IMG}/Logo-caladium.png`} alt="Caladium Systems" />
                </div>
                {heroNodes.map((n) => (
                  <div
                    key={n.key}
                    className={`orbit__node orbit__node--${n.pos} orbit__node--mini`}
                  >
                    <div className="orbit__badge orbit__badge--solid">
                      <Icon name={n.icon} size={22} />
                    </div>
                    <span className="orbit__label">{n.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== WHO WE ARE =================== */}
      <section className="section who">
        <div className="container container--wide who__grid">
          <div className="who__copy">
            <span className="eyebrow">WHO WE ARE</span>
            <h2 className="h2">We Build Intelligent Experience Ecosystems</h2>
            <p>
              At Caladium Systems, we blend emotion, technology and creativity to
              build platforms and experiences that empower millions of users,
              elevate vendors, and create unforgettable celebrations.
            </p>
            <p>
              From weddings to cultural experiences, from venue discovery to
              AI-powered planning &mdash; we are building the infrastructure for
              a new era of experiences.
            </p>
            <Link to="/#brands" className="btn btn--outline">
              Explore Our Brands <Icon name="arrow-right" size={16} />
            </Link>
          </div>

          <div className="pillars">
            {pillars.map((p) => (
              <div className="pillar" key={p.title}>
                <span className="pillar__icon">
                  <Icon name={p.icon} size={26} />
                </span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== WHY CALADIUM =================== */}
      <section className="why">
        <div className="container container--wide">
          <div
            className="why__panel"
            style={{ backgroundImage: `url(${IMG}/about/about-2.jpeg)` }}
          >
            <span className="why__veil" />
            <div className="why__copy">
              <span className="eyebrow">THE MEANING OF CALADIUM</span>
              <h2 className="h2">
                Why We Are Called <span className="grad-pink">Caladium</span>
              </h2>
              <p>
                Caladium is a plant known for its vibrant leaves, resilience and
                ability to thrive in diverse environments.
              </p>
              <p>
                It symbolizes transformation, uniqueness, beauty and growth
                &mdash; just like the ecosystems we build.
              </p>

              <div className="trait-row">
                {traits.map((t) => (
                  <div className="trait" key={t.title}>
                    <span className="trait__icon">
                      <Icon name={t.icon} size={22} />
                    </span>
                    <strong>{t.title}</strong>
                    <span className="trait__sub">{t.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== CORE VALUES =================== */}
      <section className="section values-section">
        <div className="container container--wide">
          <header className="section__head">
            <h2 className="h2">Our Core Values</h2>
            <p className="section__sub">
              The principles that guide every decision, every innovation and
              every experience we create.
            </p>
          </header>

          <div className="values">
            {values.map((v) => (
              <div className="value" key={v.title}>
                <span className="value__icon">
                  <Icon name={v.icon} size={30} />
                </span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== ECOSYSTEM FIRST =================== */}
      <section className="section eco">
        <div className="container container--wide eco__grid">
          <div className="eco__copy">
            <span className="eyebrow">OUR BUSINESS MODEL</span>
            <h2 className="h2">An Ecosystem-First Approach</h2>
            <p>
              We build, operate and scale interconnected platforms that create
              value for all stakeholders in the experience economy.
            </p>
            <ul className="bullets">
              {bullets.map((b) => (
                <li key={b}>
                  <span className="bullets__dot">
                    <Icon name="check" size={14} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="eco__visual">
            <div className="orbit orbit--big">
              <span className="orbit__ring orbit__ring--1" />
              <span className="orbit__ring orbit__ring--2" />
              <div className="orbit__core orbit__core--lotus">
                <img src={`${IMG}/Logo-caladium.png`} alt="Caladium" />
              </div>
              {ecoNodes.map((n) => (
                <div
                  key={n.key}
                  className={`orbit__node orbit__node--${n.pos} eco-node`}
                >
                  <div className="orbit__badge orbit__badge--solid">
                    <Icon name={n.icon} size={22} />
                  </div>
                  <strong className="eco-node__title">{n.label}</strong>
                  <span className="eco-node__sub">{n.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== GLOBAL AMBITION =================== */}
      <section className="global">
        <div className="container container--wide">
          <div
            className="global__panel"
            style={{ backgroundImage: `url(${IMG}/about/about-3.jpeg)` }}
          >
            <span className="global__veil" />
            <div className="global__copy">
              <span className="eyebrow">GLOBAL AMBITION</span>
              <h2 className="h2">Building for the World, from India</h2>
              <p>
                Headquartered in India, we are building global ecosystems that
                celebrate diversity, culture and human connections.
              </p>

              <div className="global__stats">
                {globalStats.map((s) => (
                  <div className="g-stat" key={s.label}>
                    <span className="g-stat__icon">
                      <Icon name={s.icon} size={22} />
                    </span>
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
